import {
  FormControl,
  FormLabel,
  HStack,
  Image,
  Input,
  Text,
  VStack,
} from '@chakra-ui/react';
import React from 'react';
import {
  ControllerRenderProps,
  FieldArrayWithId,
  FieldValues,
  Path,
  useFieldArray,
  UseFieldArrayAppend,
  UseFieldArrayRemove,
  UseFormReturn,
} from 'react-hook-form';

import { TypeUtils } from '../../../framework/utils/UtilityTypes';
import { ExtendedFormArrayProps } from '../../hooks/formItems';
import { usePostAttachedFilePreview } from '../../hooks/queries/attachedFile';
import { EngeneFileObject } from '../../models/EngeneFileObject';
import { FileUtils } from '../../utils/FileUtils';
import FormItem from '../FormItem';
import { ClearButton, SelectButton } from '../StyledButton';

/**
 *
 */
type FormFilesProps<S extends FieldValues = FieldValues> =
  ExtendedFormArrayProps<S> & {
    existingFileNames?: string[];
    form: UseFormReturn<S>;
    onDelete?: Consumer<string>;
    onChange?: Consumer<Array<unknown>>;
    prefix: string;
    startIndex: number;
    visibleFilename?: boolean;
  };

/**
 *
 * @returns
 */
const FormFiles = <S extends FieldValues>({
  existingFileNames = [],
  form,
  formControlProps,
  formLabelProps,
  label,
  onChange = () => null,
  onDelete = () => null,
  prefix,
  startIndex = 0,
  ...props
}: FormFilesProps<S>) => {
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: props.name,
  });
  const inputRef = React.useRef<HTMLInputElement>(null);
  React.useEffect(() => {
    onChange(fields);
  }, [fields]);
  const postAttachedFilePreview = usePostAttachedFilePreview();

  return (
    <FormControl textAlign="left" {...formControlProps}>
      <FormLabel
        fontWeight="semibold"
        hidden={TypeUtils.isNilString(label)}
        htmlFor={props.name}
        marginBottom={0}
        {...formLabelProps}
      >
        {label}
      </FormLabel>
      {fields.map((field, idx, array) => (
        <MemoizedFormFile
          append={append}
          field={field}
          fields={array}
          form={form}
          idx={idx}
          key={idx}
          onDelete={onDelete}
          // onSelect={postAttachedFilePreview}
          prefix={prefix}
          remove={remove}
          startIndex={startIndex}
          {...props}
        />
      ))}
      <VStack justifyContent="center">
        <Input
          hidden
          onChange={(e) => {
            console.debug('started onChange to add file');
            const images = e.target.files;
            if (!TypeUtils.isNil(images)) {
              Array.from(images).map((image) => {
                if (existingFileNames.includes(image.name)) {
                  // 既存ファイル (ここには入らないはず)
                  append(EngeneFileObject.fromFile(image) as any);
                } else if (image.type === 'application/pdf') {
                  const fileObj = EngeneFileObject.fromSelectedAndGet(
                    image,
                    prefix,
                    Math.max(
                      startIndex,
                      FileUtils.getNextIndexFrom(
                        fields as unknown as Array<EngeneFileObject>
                      )
                    ),
                    postAttachedFilePreview
                  );
                  console.debug('added file:', fileObj);
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  append(fileObj as any);
                } else {
                  const fileObj = EngeneFileObject.fromSelected(
                    image,
                    prefix,
                    Math.max(
                      startIndex,
                      FileUtils.getNextIndexFrom(
                        fields as unknown as Array<EngeneFileObject>
                      )
                    )
                  );
                  console.debug('added file:', fileObj);
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  append(fileObj as any);
                }
              });
            }
            console.debug('finished onChange to add file');
          }}
          onClick={(e) => {
            console.debug('started onClick to add file');
            // これがないと，クリア後に同じファイルをアップロードできない
            (e.target as EventTarget & HTMLInputElement).value = '';
            console.debug('finished onClick to add file');
          }}
          ref={inputRef}
          type="file"
        />
        {props.mode === 'edit' && (
          <SelectButton
            onClick={() => inputRef.current?.click()}
            type="button"
          />
        )}
      </VStack>
      {/* <AddButton
        hidden={props.mode === 'view' || props.readonly}
        isDisabled={!hidden}
        onClick={() => (fields.length > 0 ? toggle() : null)}
      /> */}
    </FormControl>
  );
};

type FormFileType = <S extends FieldValues>(
  props: FormFileProps<S>
) => JSX.Element;
type FormFileProps<S extends FieldValues> = FormFilesProps<S> & {
  append: UseFieldArrayAppend<S>;
  field: FieldArrayWithId<S>;
  fields: Array<FieldArrayWithId<S>>;
  idx: number;
  remove: UseFieldArrayRemove;
};

const FormFile = <S extends FieldValues>({
  append,
  field,
  fields,
  form,
  idx,
  onDelete = () => null,
  prefix,
  remove,
  startIndex = 0,
  visibleFilename,
  ...props
}: FormFileProps<S>) => {
  const renderNode = React.memo<{ field: ControllerRenderProps<S> }>(
    ({ field: { value, ...innerField } }) => {
      if (!EngeneFileObject.isInstance(value)) {
        return <></>;
      }
      const newFileName =
        value.newFileName ||
        `${prefix}_${idx + 1}.${value.fileName.split('.').reverse()[0]}`;
      return (
        <VStack width="100%">
          {value.toPreview && TypeUtils.isNil(value.previewFile) ? (
            <Text>プレビュー作成中です</Text>
          ) : (
            <Image
              src={value.objectUrl}
              {...innerField}
              maxWidth="calc(95% - 60px)"
            />
          )}
          <HStack justifyContent="space-around" width="100%">
            <Text hidden={!visibleFilename}>
              {TypeUtils.isNil(value.fileId)
                ? `(${value.newFileName || newFileName}) ${value.fileName}`
                : value.fileName}
            </Text>
            <ClearButton
              onClick={() => {
                // ファイル名を振り直すために一度すべて消して入れ直す
                remove();
                if (!TypeUtils.isNilNumber(value.fileId)) {
                  // id があるということは保存済ファイル
                  onDelete(value.fileName);
                }
                // 振り直すので fields の最大値を取る必要はない
                let newIndex = startIndex;
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (append as any)(
                  fields
                    .filter((_, index) => idx !== index)
                    .map((thisField: unknown) => {
                      if (!EngeneFileObject.isInstance(thisField)) {
                        return thisField;
                      }
                      if (TypeUtils.isNilString(thisField.newFileName)) {
                        // 既存
                        return EngeneFileObject.fromObject(thisField);
                      } else {
                        thisField.newFileName =
                          EngeneFileObject.generateNewFileName(
                            prefix,
                            // thisField.fileName.split('.').reverse()[0],
                            'pdf',
                            newIndex++
                          );
                        return EngeneFileObject.fromObject(thisField);
                      }
                    })
                );
              }}
              width="90px"
            />
          </HStack>
        </VStack>
      );
    }
  );

  return (
    <FormItem
      control={form.control}
      key={field.id}
      renderNode={renderNode}
      viewNode={({ field: { value, ...innerField } }) => (
        <VStack width="100%">
          <Image
            src={value.objectUrl}
            {...innerField}
            maxWidth="calc(95% - 60px)"
          />
          <Text hidden={!visibleFilename}>{value.fileName}</Text>
        </VStack>
      )}
      {...props}
      name={`${props.name}.${idx}` as Path<S>}
    />
  );
};
const MemoizedFormFile = FormFile; // React.memo(FormFile) as FormFileType;

export default React.memo(FormFiles);
